/**
 * Архипелаг режимов на первом экране лендинга — модели из блоков на CSS 3D (движок js/voxel.js).
 *
 * В центре — остров с блоком-киви, из-за которого выглядывает SPY_me: на компьютере в 3D по
 * его скину, на телефоне и при «меньше движения» — готовой картинкой files/spyme.webp. Вокруг —
 * островки режимов SkyBlock, BedWars, SkyWars и Анархия; на узком экране их нет, только центр.
 *
 * Камера неподвижна, поэтому у блоков создаются лишь видимые ей грани: верх, юг и восток. Острова
 * покачиваются со сдвигом по фазе, вся сцена чуть наклоняется за курсором, а персонаж всегда
 * смотрит на зрителя: его карточка повёрнута обратно повороту камеры и наклону. Под курсором
 * островок показывает название и онлайн своего режима, нажатие прокручивает к описанию режима.
 *
 * Первый заход за сессию: острова по очереди всплывают снизу, последним из-за острова
 * выглядывает персонаж. Вне экрана анимации стоят. Сцена декоративная (aria-hidden): те же
 * режимы и ссылки есть текстом в разделе «Режимы».
 */
import { el, leaveView, onLeave, reducedMotion } from "../ui.js";
import { FIXED_VIEW_FACES, buildBlocks, followPointer } from "../voxel.js";
import { skinStage } from "../skin3d.js";
import { MODES, plural } from "./content.js";

/** Кубики по слоям: [y, вид, строки слоя по z — от дальней к ближней], «#» — блок; extra — отдельные блоки. */
function layers(spec, extra = []) {
  const blocks = [];
  for (const [y, kind, rows] of spec) {
    rows.forEach((row, z) => [...row].forEach((cell, x) => {
      if (cell === "#") blocks.push([x, y, z, kind]);
    }));
  }
  return blocks.concat(extra);
}

/**
 * Острова архипелага.
 * blocks — кубики [x, y, z, вид]; center — точка модели в начале её координат;
 * at — место в сцене в блоках (x вправо, y вниз, z к зрителю); bob — период и фаза покачивания
 * в секундах; rise — задержка всплытия в мс; top — высота подписи над центром в блоках;
 * mode — режим, к которому ведёт островок.
 */
const ISLANDS = [
  {
    id: "home",
    blocks: layers([
      [-3, "dirt", ["-----", "-----", "--#--", "-----", "-----"]],
      [-2, "dirt", ["-----", "-###-", "-###-", "-###-", "-----"]],
      [-1, "dirt", ["-###-", "#####", "#####", "#####", "-###-"]],
      [0, "grass", ["-###-", "#####", "#####", "#####", "-###-"]],
    ], [[3, 1, 3, "kiwi"]]),
    center: { x: 2, y: 0.5, z: 2 },
    at: [0, 1.4, 0],
    bob: [7, 0],
    rise: 0,
  },
  {
    id: "skyblock",
    mode: "skyblock",
    blocks: layers([
      [-2, "dirt", ["---", "-#-", "---"]],
      [-1, "dirt", ["##-", "###", "-##"]],
      [0, "grass", ["##-", "###", "-##"]],
    ], [
      [0, 1, 0, "log"], [0, 2, 0, "log"], [0, 3, 0, "log"],
      [1, 3, 0, "leaves"], [0, 3, 1, "leaves"], [1, 3, 1, "leaves"], [0, 4, 0, "leaves"],
      [2, 1, 2, "chest"],
    ]),
    center: { x: 1, y: 0.5, z: 1 },
    at: [-7, -3.6, -4],
    bob: [8.5, -3.1],
    rise: 160,
    top: 4.6,
  },
  {
    id: "bedwars",
    mode: "bedwars",
    blocks: layers([
      [-2, "cobble", ["---", "-#-", "---"]],
      [-1, "wool", ["-#-", "###", "-#-"]],
      [0, "wool", ["###", "###", "###"]],
    ], [[1, 1, 0, "bedHead"], [1, 1, 1, "bedFoot"]]),
    center: { x: 1, y: 0.5, z: 1 },
    at: [7, -3, -3],
    bob: [7.6, -1.4],
    rise: 280,
    top: 2.4,
  },
  {
    id: "skywars",
    mode: "skywars",
    blocks: layers([
      [-2, "cobble", ["---", "-#-", "---"]],
      [-1, "cobble", ["-##", "###", "#--"]],
      [0, "grass", ["-##", "###", "##-"]],
    ], [[1, -1, 2, "gold"], [1, 1, 1, "chest"]]),
    center: { x: 1, y: 0.5, z: 1 },
    at: [-7.4, 4.6, 1.5],
    bob: [9, -5.2],
    rise: 400,
    top: 2.4,
  },
  {
    id: "anarchy",
    mode: "anarchy",
    blocks: layers([
      [-2, "netherrack", ["---", "-#-", "---"]],
      [-1, "netherrack", ["##-", "###", "-#-"]],
      [0, "netherrack", ["###", "###", "-##"]],
    ], [[0, 1, 0, "tnt"], [0, 2, 0, "tnt"], [2, 1, 1, "tnt"]]),
    center: { x: 1, y: 0.5, z: 1 },
    at: [7.4, 5, 0.5],
    bob: [8.2, -2.4],
    rise: 520,
    top: 3.4,
  },
];

/** Названия режимов для подписей. */
const TITLES = Object.fromEntries(MODES.map((mode) => [mode.id, mode.title]));

/**
 * Поставить архипелаг в контейнер stage.
 *
 * @param {HTMLElement} stage
 * @param {object} options
 * @param {boolean} options.intro  острова всплывают по очереди (первый заход за сессию)
 * @returns {{ setOnline: (online: Record<string, number>) => void, setSkin: (hash: string|null) => void }}
 */
export function mountArchipelago(stage, { intro = false } = {}) {
  const still = reducedMotion();
  const wide = window.matchMedia("(min-width: 768px)");
  const online = {};
  let skinHash = null;
  let cameo = null;
  let cleanups = [];
  let label = null;

  /** Карточка персонажа: выглядывает из-за дальнего края центрального острова. */
  function cameoNode() {
    cameo = skinStage({
      hash: skinHash,
      name: "SPY_me",
      greet: true,
      hint: false,
      backdrop: false,
      fallbackSrc: "/files/spyme.webp",
      touchLaunch: false,
      view: { zoom: 1.25, offsetY: -4, yaw: 0.35 },
    });
    // На компьютере персонаж машет, когда на него наводят курсор.
    let waved = 0;
    cameo.node.addEventListener("pointerenter", () => {
      if (Date.now() - waved < 3000) return;
      waved = Date.now();
      cameo.wave();
    });
    return el("div", { class: "arch__cameo" }, el("div", { class: "arch__peek" }, cameo.node));
  }

  function islandNode(island) {
    const view = el("div", { class: "arch__view" },
      buildBlocks(island.blocks, { center: island.center, faces: FIXED_VIEW_FACES }));
    if (island.mode) view.appendChild(el("div", { class: "arch__anchor", style: `--top:${island.top}` }));
    if (island.id === "home") view.appendChild(cameoNode());
    return el("div", {
      class: `arch__island${island.mode ? " arch__island--mode" : ""}`,
      "data-mode": island.mode || null,
      style: `--x:${island.at[0]};--y:${island.at[1]};--z:${island.at[2]};`
        + `--bob-time:${island.bob[0]}s;--bob-delay:${island.bob[1]}s;--rise-delay:${island.rise}ms`,
    }, el("div", { class: "arch__rise" }, el("div", { class: "arch__bob" }, view)));
  }

  /** Подпись островка: название режима и онлайн, над самим островком. */
  function showLabel(island) {
    const mode = island.dataset.mode;
    const anchor = island.querySelector(".arch__anchor");
    if (!anchor || !label) return;
    const count = online[mode] || 0;
    label.replaceChildren(
      el("span", { class: "arch__label-title" }, TITLES[mode]),
      el("span", { class: "arch__label-meta" }, count > 0 ? `${count} ${plural(count, "играет", "играют", "играют")}` : "Описание режима ниже"),
    );
    const box = stage.getBoundingClientRect();
    const point = anchor.getBoundingClientRect();
    label.style.left = `${point.left - box.left}px`;
    label.style.top = `${point.top - box.top}px`;
    label.hidden = false;
  }

  function build() {
    cleanups.forEach((cleanup) => cleanup());
    cleanups = [];
    // Прежняя сцена закрывается целиком: 3D-камео освобождает WebGL-контекст.
    if (stage.firstElementChild) leaveView(stage.firstElementChild);
    const islands = wide.matches ? ISLANDS : ISLANDS.filter((island) => island.id === "home");
    const tilt = el("div", { class: "arch__tilt" }, islands.map(islandNode));
    label = el("div", { class: "arch__label", hidden: true });
    const scene = el("div", { class: `arch${still ? " arch--still" : ""}${intro && !still ? " arch--intro" : ""}` }, [tilt, label]);
    stage.replaceChildren(scene);
    // Всплытие только при первом построении; после смены ширины окна острова просто на месте.
    intro = false;

    // Островки режимов ведут к описанию режима; подпись — под курсором.
    const pick = (event) => event.target instanceof Element && event.target.closest(".arch__island--mode");
    scene.addEventListener("pointerover", (event) => {
      const island = pick(event);
      if (island) showLabel(island);
    });
    scene.addEventListener("pointerout", (event) => {
      const from = pick(event);
      const to = event.relatedTarget instanceof Element && event.relatedTarget.closest(".arch__island--mode");
      if (from && from !== to) label.hidden = true;
    });
    // Переход по якорю: прокрутка (плавная, если не просили меньше движения — это решает CSS)
    // и подсветка карточки режима через :target.
    scene.addEventListener("click", (event) => {
      const island = pick(event);
      if (island) location.hash = `mode-${island.dataset.mode}`;
    });

    if (still) return;
    // Вне экрана анимации стоят — не тратим батарею на невидимую сцену.
    const observer = new IntersectionObserver(([entry]) => scene.classList.toggle("is-paused", !entry.isIntersecting));
    observer.observe(stage);
    cleanups.push(() => observer.disconnect());
    if (window.matchMedia("(pointer: fine)").matches) cleanups.push(followPointer(stage, tilt, { yaw: 14, pitch: 8 }));
  }

  build();
  // Повернули телефон или растянули окно — архипелаг перестраивается под новую ширину.
  const rebuild = () => build();
  wide.addEventListener("change", rebuild);
  onLeave(stage, () => {
    wide.removeEventListener("change", rebuild);
    cleanups.forEach((cleanup) => cleanup());
  });

  return {
    /** Онлайн по режимам для подписей островков. */
    setOnline(next) {
      Object.assign(online, next);
    },
    /** Hash текстуры скина SPY_me (из API сайта) для 3D-камео. */
    setSkin(hash) {
      skinHash = hash;
      if (cameo) cameo.setSkin(hash);
    },
  };
}
